import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

interface Step {
  num: string;
  title: string;
  desc: string;
}

const steps: Step[] = [
  { num: '1', title: 'Apply anywhere', desc: 'Gmail detects your confirmation email automatically' },
  { num: '2', title: 'We find the right recruiters', desc: 'Web search + database lookup surfaces contacts at every company you apply to' },
  { num: '3', title: 'Drafts land in your inbox', desc: 'Review and send, or let them go automatically' },
];

export default function SignIn() {
  const [visible, setVisible] = useState(false);
  const { login } = useAuth();

  useEffect(() => { requestAnimationFrame(() => setVisible(true)); }, []);

  return (
    <div className="h-[100dvh] lg:h-auto lg:min-h-screen flex overflow-hidden lg:overflow-visible">
      {/* LEFT PANEL */}
      <div
        className="hidden lg:flex flex-col justify-between w-[55%] p-12 relative overflow-hidden"
        style={{
          backgroundColor: 'var(--sand)',
          backgroundImage: 'radial-gradient(circle, #c5bdb5 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
      >
        {/* Logo */}
        <Link
          to="/"
          className="inline-flex items-center gap-2.5"
          style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.4s ease' }}
        >
          <img
            src="/Eextramile1.png"
            alt="Eextramile AI"
            className="h-10 w-auto object-contain flex-shrink-0"
          />
          <span className="font-bold text-ink text-xl tracking-tight">
            Eextramile <span className="text-g">AI</span>
          </span>
        </Link>

        {/* Hero */}
        <div
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.5s ease 0.1s, transform 0.5s ease 0.1s',
          }}
        >
          <h1 className="text-5xl font-bold leading-tight text-ink mb-4" style={{ fontFamily: 'Instrument Serif, serif' }}>
            Your job search,
            <br />
            <em className="not-italic italic text-g">on autopilot.</em>
          </h1>
          <p className="text-muted text-base leading-relaxed mb-10 max-w-md">
            Apply to a job and EextraMile automatically finds recruiters, guesses their emails, and drafts personalized cold outreach — all before you finish your coffee.
          </p>

          <div className="space-y-6">
            {steps.map(({ num, title, desc }, i) => (
              <div
                key={num}
                className="flex gap-4 items-start"
                style={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? 'translateY(0)' : 'translateY(16px)',
                  transition: `opacity 0.4s ease ${0.2 + i * 0.1}s, transform 0.4s ease ${0.2 + i * 0.1}s`,
                }}
              >
                <div className="h-8 w-8 rounded-full bg-g-light text-g border border-g-border text-sm font-bold flex items-center justify-center flex-shrink-0">
                  {num}
                </div>
                <div>
                  <p className="font-semibold text-ink text-sm">{title}</p>
                  <p className="text-muted text-xs mt-0.5 leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="text-xs text-hint">© 2026 EextraMile</p>
      </div>

      {/* RIGHT PANEL */}
      <div className="flex-1 flex flex-col items-center justify-center h-full lg:min-h-screen px-6 pt-[max(1.5rem,env(safe-area-inset-top))] pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:p-8 lg:p-12 bg-card overflow-x-hidden">
        <div
          className="w-full max-w-[390px] lg:max-w-[360px] flex flex-col my-auto"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(16px)',
            transition: 'opacity 0.5s ease 0.15s, transform 0.5s ease 0.15s',
          }}
        >
          {/* Mobile hero brand & value prop */}
          <div className="lg:hidden text-left mb-3.5">
            <Link to="/" className="inline-flex items-center gap-2.5 mb-3">
              <img src="/Eextramile1.png" alt="Eextramile AI" className="h-8 w-auto object-contain flex-shrink-0" />
              <span className="font-bold text-ink text-xl tracking-tight">
                Eextramile <span className="text-g">AI</span>
              </span>
            </Link>
            <h1 className="text-[28px] sm:text-3xl font-semibold text-ink leading-[1.14] mb-1.5" style={{ fontFamily: 'Instrument Serif, serif' }}>
              Your job search,
              <br />
              <em className="italic text-g not-italic">on autopilot.</em>
            </h1>
            <p className="text-xs sm:text-sm text-muted leading-relaxed">
              Finds recruiters and drafts personalized outreach in your Gmail.
            </p>
            <div className="w-full h-px bg-border mt-3.5" />
          </div>

          <div className="mb-3.5">
            <p className="text-[11px] font-semibold text-hint uppercase tracking-[0.16em] mb-1">WELCOME</p>
            <h2 className="text-2xl sm:text-3xl font-semibold text-ink leading-tight mb-1" style={{ fontFamily: 'Instrument Serif, serif' }}>
              Sign In to EextraMile
            </h2>
            <p className="text-muted text-xs sm:text-sm leading-relaxed">
              Connect your Google account to start automating your recruiter outreach.
            </p>
          </div>

          {/* Action buttons */}
          <div className="space-y-2.5 mb-3.5">
            <button
              onClick={login}
              className="w-full flex items-center justify-center gap-2.5 bg-ink text-white py-3 px-4 rounded-[10px] text-sm font-medium hover:bg-[#292524] transition-all shadow-sm active:scale-[0.99]"
              style={{ boxShadow: '0 1px 3px rgba(28,25,23,0.15)' }}
            >
              {/* Colored Google G */}
              <svg viewBox="0 0 24 24" className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Continue with Google
            </button>

            {/* Demo login */}
            <a
              href={`${process.env.REACT_APP_API_URL ?? ''}/api/auth/demo`}
              className="w-full flex items-center justify-center gap-2 border border-border bg-white text-ink py-3 px-4 rounded-[10px] text-sm font-medium hover:bg-sand transition-all active:scale-[0.99]"
            >
              <svg className="h-4 w-4 flex-shrink-0 text-ink" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.964-7.178z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              View Demo
            </a>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 mb-3.5">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-hint">Permissions requested</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* Permissions */}
          <div className="rounded-xl border border-border/80 p-3 mb-3.5 bg-[#faf9f6]">
            <p className="text-[10px] font-semibold text-hint uppercase tracking-wider mb-1.5">We'll ask for access to</p>
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-lg bg-white border border-border/80 flex items-center justify-center flex-shrink-0 shadow-2xs">
                <svg className="h-4 w-4 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                </svg>
              </div>
              <div>
                <p className="text-xs font-semibold text-ink leading-none">Gmail</p>
                <p className="text-[11px] text-muted mt-0.5 leading-tight">Read confirmation emails & create drafts</p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <p className="text-xs text-hint text-center leading-relaxed">
            By signing in you agree to our{' '}
            <Link to="/terms" className="underline hover:text-ink">
              Terms
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="underline hover:text-ink">
              Privacy Policy
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
